<template>
  <div class="basis-map-demo" v-loading="loading">
    <div class="demo-layout">
      <section class="map-stage-card">
        <div class="map-toolbar">
          <div class="map-toolbar__copy">
            <h3>高德地图基础案例</h3>
            <p>覆盖数据落点、中心点切换、缩放控制、地图点击事件、逆地址解析和天地图图层切换。</p>
          </div>
          <div class="map-toolbar__status">
            <span class="status-chip">中心点：{{ currentCenterText }}</span>
            <span class="status-chip">缩放级别：{{ currentZoom }}</span>
            <span class="status-chip">当前底图：{{ currentLayerLabel }}</span>
          </div>
        </div>

        <div ref="mapContainer" class="map-stage"></div>
      </section>

      <aside class="side-panel">
        <div class="panel-card">
          <div class="panel-card__title">基础控制</div>

          <div class="field-block">
            <div class="field-label">地图中心点</div>
            <el-select v-model="selectedCenterKey" style="width: 100%">
              <el-option
                v-for="item in centerOptions"
                :key="item.key"
                :label="item.label"
                :value="item.key"
              />
            </el-select>
            <div class="field-actions">
              <el-button type="primary" size="small" @click="applyCenterPreset">切换中心点</el-button>
            </div>
          </div>

          <div class="field-block">
            <div class="field-label">地图级别</div>
            <el-slider
              v-model="selectedZoom"
              :min="3"
              :max="18"
              show-input
              @change="applyZoomLevel"
            />
          </div>

          <div class="field-block">
            <div class="field-label">底图切换</div>
            <el-radio-group style="display: flex;" v-model="selectedLayerKey" size="small" @change="applyLayerPreset">
              <el-radio-button
                v-for="item in layerOptions"
                :key="item.key"
                :label="item.key"
              >
                {{ item.label }}
              </el-radio-button>
            </el-radio-group>
          </div>

          <div class="field-block">
            <div class="field-label">数据落点</div>
            <div class="field-actions">
              <el-button type="primary" size="small" icon="el-icon-position" @click="loadBusinessMarkers">
                加载示例点位
              </el-button>
              <el-button size="small" icon="el-icon-delete" @click="clearBusinessMarkers">
                清空点位
              </el-button>
            </div>
          </div>
        </div>

        <div class="panel-card">
          <div class="panel-card__title">点击结果</div>
          <div class="result-grid">
            <div class="result-item">
              <span>点击坐标</span>
              <strong>{{ clickCoordinateText }}</strong>
            </div>
            <div class="result-item">
              <span>逆地址信息</span>
              <strong class="address-text">{{ clickAddress || '点击地图后自动反查地址' }}</strong>
            </div>
          </div>
          <div class="field-actions">
            <el-button size="small" icon="el-icon-location-outline" @click="focusClickMarker" :disabled="!clickMarker">
              定位点击 Marker
            </el-button>
          </div>
        </div>

        <div class="panel-card">
          <div class="panel-card__title">Marker 点击事件</div>
          <div v-if="selectedMarker" class="marker-detail">
            <div class="marker-detail__row">
              <span class="marker-detail__label">名称</span>
              <span>{{ selectedMarker.name }}</span>
            </div>
            <div class="marker-detail__row">
              <span class="marker-detail__label">类型</span>
              <span>{{ selectedMarker.type }}</span>
            </div>
            <div class="marker-detail__row">
              <span class="marker-detail__label">坐标</span>
              <span>{{ formatLngLat(selectedMarker.position) }}</span>
            </div>
            <div class="marker-detail__row">
              <span class="marker-detail__label">说明</span>
              <span>{{ selectedMarker.description }}</span>
            </div>
          </div>
          <div v-else class="marker-placeholder">点击任意示例 Marker 后，这里会展示当前点位信息。</div>
        </div>
      </aside>
    </div>
  </div>
</template>

<script>
import AMapLoader from '@amap/amap-jsapi-loader'

// 高德地图 Web JS API Key。
const AMAP_KEY = '0826878f934aa6f773237d9c82dfcf45'
// 天地图瓦片服务 Key，用于演示在高德 Map 中叠加天地图底图。
const TDT_KEY = '5548e2c926e88b724376d398ba5476ca'

export default {
  name: 'BasisMapDemo',
  data() {
    return {
      // 页面整体加载状态，主要用于地图 SDK 初始化阶段。
      loading: false,
      // 高德地图 SDK 命名空间对象，后续创建 Map/Marker/TileLayer 都依赖它。
      AMap: null,
      // 当前地图实例。
      map: null,
      // 高德逆地址解析实例，处理“点击地图后反查地址”。
      geocoder: null,
      // 高德信息窗体实例，处理业务 Marker 点击后的浮层。
      infoWindow: null,
      // 地图点击后创建的那个临时 Marker。
      clickMarker: null,
      // 当前地图上已经渲染的业务 Marker 列表。
      businessMarkers: [],
      // 当前被点击的业务 Marker 数据，用于右侧详情展示。
      selectedMarker: null,
      // 地图点击得到的坐标文本。
      clickCoordinateText: '-',
      // 当前点击位置的逆地址解析结果。
      clickAddress: '',
      // 当前地图中心点坐标。
      currentCenter: [116.397428, 39.90923],
      // 当前地图真实缩放级别。
      currentZoom: 11,
      // 当前选中的中心点预设 key。
      selectedCenterKey: 'beijing',
      // 右侧控件里选中的缩放级别。
      selectedZoom: 11,
      // 当前选中的底图类型 key。
      selectedLayerKey: 'gaode-vector',
      // 当前正在地图上生效的图层数组。
      activeLayers: [],
      // 中心点预设列表，演示“地图中心点如何切换”。
      centerOptions: [
        { key: 'beijing', label: '北京 · 天安门', center: [116.397428, 39.90923] },
        { key: 'shanghai', label: '上海 · 人民广场', center: [121.473701, 31.230416] },
        { key: 'guangzhou', label: '广州 · 珠江新城', center: [113.32459, 23.10668] },
        { key: 'shenzhen', label: '深圳 · 市民中心', center: [114.057868, 22.543099] }
      ],
      // 底图预设列表，既包含高德默认底图，也包含天地图图层组合。
      layerOptions: [
        { key: 'gaode-vector', label: '高德矢量' },
        { key: 'gaode-satellite', label: '高德卫星' },
        { key: 'tianditu-vector', label: '天地图矢量' },
        { key: 'tianditu-image', label: '天地图影像' }
      ],
      // 示例业务点位源数据，用来演示数据落点和 Marker 点击事件。
      markerSource: [
        {
          id: 'm-1',
          name: '北京总部',
          type: '办公点',
          position: [116.404, 39.915],
          description: '示例办公点，演示基础数据落点和点击事件。'
        },
        {
          id: 'm-2',
          name: '朝阳服务站',
          type: '服务站',
          position: [116.453, 39.939],
          description: '示例服务站，点击后会弹出说明面板。'
        },
        {
          id: 'm-3',
          name: '丰台仓配点',
          type: '仓储点',
          position: [116.29, 39.858],
          description: '示例仓储点，适合演示业务 Marker 基础能力。'
        }
      ]
    }
  },
  computed: {
    // 将当前中心点格式化成可直接展示的经纬度文本。
    currentCenterText() {
      return this.formatLngLat(this.currentCenter)
    },
    // 根据当前图层 key 反查右上角要显示的底图名称。
    currentLayerLabel() {
      const target = this.layerOptions.find(item => item.key === this.selectedLayerKey)
      return target ? target.label : '-'
    }
  },
  mounted() {
    // 组件挂载后立即初始化地图。
    this.initMap()
  },
  beforeDestroy() {
    // 组件销毁前清理覆盖物和地图实例，避免内存泄漏。
    this.clearBusinessMarkers()
    if (this.clickMarker && this.map) {
      this.map.remove(this.clickMarker)
    }
    if (this.map) {
      this.map.destroy()
      this.map = null
    }
  },
  methods: {
    // 初始化高德地图、工具控件、逆地址解析器，并加载默认业务点位。
    async initMap() {
      this.loading = true
      try {
        const AMap = await AMapLoader.load({
          key: AMAP_KEY,
          version: '2.0',
          plugins: ['AMap.Scale', 'AMap.ToolBar', 'AMap.Geocoder']
        })

        this.AMap = AMap
        this.map = new AMap.Map(this.$refs.mapContainer, {
          zoom: this.selectedZoom,
          center: this.currentCenter,
          resizeEnable: true,
          viewMode: '2D'
        })

        this.geocoder = new AMap.Geocoder({
          radius: 1000, //以已知坐标为中心点，radius为半径，返回范围内兴趣点和道路信息
          extensions: "all" //返回地址描述以及附近兴趣点和道路信息，默认"base"
        })
        this.geocoder.on("complete", this.geocoderCallBack);
        this.infoWindow = new AMap.InfoWindow({ offset: new AMap.Pixel(0, -24) })

        this.map.addControl(new AMap.Scale())
        this.map.addControl(new AMap.ToolBar({ position: 'RB' }))
        this.bindMapEvents()
        this.applyLayerPreset(this.selectedLayerKey)
        this.loadBusinessMarkers()
      } catch (error) {
        console.error('Failed to init basis map demo:', error)
        this.$toast.error('高德地图加载失败')
      } finally {
        this.loading = false
      }
    },
    // 统一绑定地图事件：点击地图、拖拽结束、缩放结束。
    bindMapEvents() {
      this.map.on('click', this.handleMapClick)
      this.map.on('moveend', () => {
        this.syncMapState()
      })
      this.map.on('zoomend', () => {
        this.syncMapState()
      })
    },
    // 从地图实例反向同步中心点和缩放级别到页面状态。
    syncMapState() {
      if (!this.map) return
      const center = this.map.getCenter()
      if (center) {
        this.currentCenter = [center.lng, center.lat]
      }
      this.currentZoom = this.map.getZoom()
      this.selectedZoom = this.currentZoom
    },
    // 根据右侧选择的预设中心点，切换地图中心位置。
    applyCenterPreset() {
      const target = this.centerOptions.find(item => item.key === this.selectedCenterKey)
      if (!target || !this.map) return
      this.map.setCenter(target.center)
      this.currentCenter = [...target.center]
      this.map.setZoom(this.selectedZoom)
    },
    // 根据右侧滑块设置新的缩放级别。
    applyZoomLevel() {
      if (!this.map) return
      this.map.setZoom(this.selectedZoom)
      this.currentZoom = this.selectedZoom
    },
    // 创建一个天地图瓦片图层，type 控制矢量/影像/注记类型。
    createTiandituLayer(type) {
      return new this.AMap.TileLayer({
        getTileUrl: (x, y, z) => `https://t0.tianditu.gov.cn/DataServer?T=${type}&x=${x}&y=${y}&l=${z}&tk=${TDT_KEY}`,
        tileSize: 256,
        zIndex: 4
      })
    },
    // 根据当前底图类型，拼出真正要传给 map.setLayers() 的图层数组。
    buildLayerGroup(layerKey) {
      if (layerKey === 'gaode-satellite') {
        return [new this.AMap.TileLayer.Satellite(), new this.AMap.TileLayer.RoadNet()]
      }
      if (layerKey === 'tianditu-vector') {
        return [this.createTiandituLayer('vec_w'), this.createTiandituLayer('cva_w')]
      }
      if (layerKey === 'tianditu-image') {
        return [this.createTiandituLayer('img_w'), this.createTiandituLayer('cia_w')]
      }
      return [new this.AMap.TileLayer()]
    },
    // 切换地图底图，既支持高德默认图层，也支持天地图图层组合。
    applyLayerPreset(layerKey) {
      if (!this.map || !this.AMap) return
      this.selectedLayerKey = layerKey
      this.activeLayers = this.buildLayerGroup(layerKey)
      this.map.setLayers(this.activeLayers)
    },
    // 根据一条业务数据创建 Marker，并绑定点击事件与信息窗体。
    createBusinessMarker(record) {
      const marker = new this.AMap.Marker({
        position: record.position,
        title: record.name,
        offset: new this.AMap.Pixel(-10, -20)
      })

      marker.setExtData(record)
      marker.on('click', () => {
        this.selectedMarker = record
        this.infoWindow.setContent(`
          <div style="min-width: 220px; line-height: 1.8;">
            <div style="font-weight: 700; margin-bottom: 6px;">${record.name}</div>
            <div>类型：${record.type}</div>
            <div>坐标：${this.formatLngLat(record.position)}</div>
            <div>说明：${record.description}</div>
          </div>
        `)
        this.infoWindow.open(this.map, record.position)
      })

      return marker
    },
    // 批量加载示例业务 Marker，并自动调整地图视野到全部点位范围。
    loadBusinessMarkers() {
      if (!this.map || !this.AMap) return
      this.clearBusinessMarkers()
      this.businessMarkers = this.markerSource.map(item => this.createBusinessMarker(item))
      this.map.add(this.businessMarkers)
      this.map.setFitView(this.businessMarkers)
      this.syncMapState()
    },
    // 清空当前业务 Marker、右侧选中状态和信息窗体。
    clearBusinessMarkers() {
      if (this.businessMarkers.length && this.map) {
        this.map.remove(this.businessMarkers)
      }
      this.businessMarkers = []
      this.selectedMarker = null
      if (this.infoWindow) {
        this.infoWindow.close()
      }
    },
    // 地图点击入口：读取坐标、落一个临时 Marker，并触发逆地址解析。
    handleMapClick(event) {
      if (!event || !event.lnglat || !this.map) return
      const lnglat = [event.lnglat.lng, event.lnglat.lat]
      this.clickCoordinateText = this.formatLngLat(lnglat)
      this.placeClickMarker(lnglat)
      this.reverseGeocode(lnglat)
    },
    // 在地图点击位置放置一个可复用的临时 Marker。
    placeClickMarker(position) {
      if (!this.map || !this.AMap) return
      if (!this.clickMarker) {
        this.clickMarker = new this.AMap.Marker({
          position,
          offset: new this.AMap.Pixel(-10, -20)
        })
        this.map.add(this.clickMarker)
      } else {
        this.clickMarker.setPosition(position)
      }
    },
    geocoderCallBack(status, result) {
      console.log('status, result', status, result)
    },
    // 对点击坐标做逆地址解析，拿到可读地址文本。
    async reverseGeocode(position) {
      if (!this.geocoder) return
      this.clickAddress = '地址解析中...'
      this.geocoder.getAddress(position, async (status, result) => {
        console.log('status, result', status, result)
        if (status === 'complete' && result && result.regeocode) {
          this.clickAddress = result.regeocode.formattedAddress || '未获取到地址信息'
          return
        }
        
        // JSAPI解析失败，使用HTTP接口降级
        try {
          const res = await this.$axios.get('https://restapi.amap.com/v3/geocode/regeo', {
            params: {
              output: 'json',
              location: `${position[0]},${position[1]}`,
              key: '9d9f359fb0205608576490654b711683'
            }
          })
          
          // 判断接口返回状态
          if (res.data && res.data.status === '1' && res.data.regeocode) {
            // 成功：获取详细地址
            this.clickAddress = res.data.regeocode.formatted_address || '未获取到地址信息'
            
            // 可以进一步解析地址组件（可选）
            const addressComponent = res.data.regeocode.addressComponent
            if (addressComponent) {
              console.log('详细地址信息:', {
                省份: addressComponent.province,
                城市: addressComponent.city,
                区县: addressComponent.district,
                街道: addressComponent.township,
                详细街道: addressComponent.streetNumber?.street,
                门牌号: addressComponent.streetNumber?.number,
                建筑物: addressComponent.building?.name
              })
            }
          } else {
            // 接口返回错误
            const errorMsg = res.data?.info || '地址解析失败'
            console.error('逆地理编码失败:', errorMsg)
            this.clickAddress = '地址解析失败'
          }
        } catch (error) {
          // HTTP请求失败
          console.error('逆地理编码请求失败:', error)
          this.clickAddress = '地址解析失败'
        }
      })
    },
    // 将地图视野重新定位到“地图点击产生的临时 Marker”。
    focusClickMarker() {
      if (!this.map || !this.clickMarker) return
      const position = this.clickMarker.getPosition()
      this.map.setCenter(position)
      this.map.setZoom(15)
      this.syncMapState()
    },
    // 把坐标数组格式化成统一的经纬度字符串。
    formatLngLat(position) {
      if (!position || position.length < 2) return '-'
      return `${Number(position[0]).toFixed(6)}, ${Number(position[1]).toFixed(6)}`
    }
  }
}
</script>

<style scoped lang="scss">
.basis-map-demo {
  width: 100%;
}

.demo-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 360px;
  gap: 18px;
  min-height: 680px;
}

.map-stage-card,
.panel-card {
  border: 1px solid rgba(31, 45, 61, 0.06);
  border-radius: 16px;
  background: var(--surface-1);
  box-shadow: 0 10px 28px rgba(31, 45, 61, 0.08);
}

.map-stage-card {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.map-toolbar {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 20px;
  border-bottom: 1px solid rgba(31, 45, 61, 0.06);
  background: linear-gradient(135deg, #f9fbff 0%, #eef4ff 100%);
}

.map-toolbar__copy h3 {
  margin: 0 0 8px;
  font-size: 20px;
  color: #22324a;
}

.map-toolbar__copy p {
  margin: 0;
  font-size: 13px;
  line-height: 1.7;
  color: #6d7a8a;
}

.map-toolbar__status {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: flex-end;
  gap: 10px;
}

.status-chip {
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(52, 120, 246, 0.1);
  color: #315dcf;
  font-size: 12px;
  line-height: 1;
}

.map-stage {
  flex: 1;
  min-height: 540px;
}

.side-panel {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.panel-card {
  padding: 18px;
}

.panel-card__title {
  margin-bottom: 14px;
  font-size: 16px;
  font-weight: 600;
  color: #22324a;
}

.field-block + .field-block {
  margin-top: 18px;
}

.field-label {
  margin-bottom: 10px;
  font-size: 14px;
  color: #42536a;
  font-weight: 500;
}

.field-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 10px;
}

.result-grid {
  display: grid;
  gap: 12px;
}

.result-item {
  padding: 14px 12px;
  border-radius: 12px;
  background: linear-gradient(180deg, #f7fbff 0%, #f2f6fb 100%);
  border: 1px solid rgba(31, 45, 61, 0.06);
}

.result-item span {
  display: block;
  margin-bottom: 8px;
  font-size: 12px;
  color: #7a8797;
}

.result-item strong {
  color: #1f2d3d;
  font-size: 14px;
  line-height: 1.6;
}

.address-text {
  display: block;
  min-height: 42px;
}

.marker-detail {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.marker-detail__row {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding-bottom: 10px;
  border-bottom: 1px solid rgba(31, 45, 61, 0.06);
  color: #334155;
  font-size: 14px;
}

.marker-detail__label {
  flex-shrink: 0;
  color: #7a8797;
}

.marker-placeholder {
  color: #7a8797;
  font-size: 14px;
  line-height: 1.8;
}

@media (max-width: 1200px) {
  .demo-layout {
    grid-template-columns: 1fr;
  }

  .map-toolbar {
    flex-direction: column;
  }

  .map-toolbar__status {
    justify-content: flex-start;
  }
}
</style>
